import React, { useEffect, useState, useMemo } from 'react';
import { Button, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Snackbar, Alert, Checkbox } from '@mui/material';
import Table from '@mui/material/Table';
import Fade from '@mui/material/Fade';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import instance from '../axios/axios';
import StatusIcon from './StatusIcon';
import ConfirmDialog from './ConfirmDialog';

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [addUser, setAddUser] = useState(false);
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userPassword, setUserPassword] = useState('');
    const [userConfirmPassword, setUserConfirmPassword] = useState('');
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [editUserReportsDialogOpen, setEditUserReportsDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userReports, setUserReports] = useState([]);
    const [availableReports, setAvailableReports] = useState([]);
    const [selectedReportToAdd, setSelectedReportToAdd] = useState('');
    const [userRoles, setUserRoles] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    const [selectedRoleToAdd, setSelectedRoleToAdd] = useState('');
    const [editUserRolesDialogOpen, setEditUserRolesDialogOpen] = useState(false);
    const [xferCRMUsers, setXferCRMUsers] = useState(false);
    const [crmUsers, setCrmUsers] = useState([]);
    const [refreshUsers, setRefreshUsers] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('info');
    const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
    const [userAttributes, setUserAttributes] = useState([]);
    const [id, setId] = useState('');
    const [email, setEmail] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [username, setUsername] = useState('');
    const [changePasswordBox, setChangePasswordBox] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changePasswordDisabled, setChangePasswordDisabled] = useState(true);
    const [editUserApplicationsDialogOpen, setEditUserApplicationsDialogOpen] = useState(false);
    const [userApplications, setUserApplications] = useState([]);
    const [availableApplications, setAvailableApplications] = useState([]);
    const [selectedApplicationToAdd, setSelectedApplicationToAdd] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isadminChange, setIsAdminChange] = useState(false);
    const [userAdminCondition, setUserAdminCondition] = useState(null);
    const [checked, setChecked] = useState(false);
    const [crmUserSearchQuery, setCrmUserSearchQuery] = useState('');
    const [selectedCrmUsers, setSelectedCrmUsers] = useState([]);
    const [isTransferring, setIsTransferring] = useState(false);

    // Filter CRM users based on search query (name and email only)
    const filteredCrmUsers = useMemo(() => {
        if (!crmUserSearchQuery.trim()) return crmUsers;
        const needle = crmUserSearchQuery.toLowerCase();
        return crmUsers.filter((user) => 
            user.name.toLowerCase().includes(needle) || 
            (user.email && user.email.toLowerCase().includes(needle))
        );
    }, [crmUsers, crmUserSearchQuery]);

    const handleAddUserClear = () => {
        setUserName('');
        setUserEmail('');
        setUserPassword('');
        setUserConfirmPassword('');
        setAddUser(false);
    };

    const handleToggleAddUser = () => {
        setAddUser(!addUser);
    }

    const handleSnackbarOpen = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleAddReportToUser = async () => {
        if (!selectedReportToAdd) {
            handleSnackbarOpen('Please select a report to add.', 'warning');
            return;
        }
    
        try {
            const response = await instance.post(`/access/add`, { user_id: selectedUser.id, report_id: selectedReportToAdd }, { withCredentials: true });
            if (response.status === 200) {
                alert('Report added successfully');
                handleEditUserReports(selectedUser);
            } else {
                handleSnackbarOpen('Failed to add report', 'error');
            }
        } catch (error) {
            console.error('Error adding report:', error);
            handleSnackbarOpen('Error adding report', 'error');
        }
    };

    const handleSetChangePassword = () => {
        setChangePasswordBox(true);
    }

    const handleChangePassword = async (id) => {
        try {
            const response = await instance.post(`/users/${id}/edit`, { password: password }, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('Password changed successfully', 'success');
                setChangePasswordBox(false);
                setPassword('');
                setConfirmPassword('');
            } else {
                handleSnackbarOpen('Failed to change password', 'error');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            handleSnackbarOpen('Error changing password', 'error');
        }
    }

    const handleAddRoleToUser = async () => {
        if (!selectedRoleToAdd) {
            handleSnackbarOpen('Please select a role to add.', 'warning');
            return;
        } else {
            try {
                const response = await instance.post(`/users/roles/members/add`, { user_id: selectedUser.id, role_id: selectedRoleToAdd }, { withCredentials: true });
                if (response.status === 200) {
                    handleSnackbarOpen('Role added successfully', 'success');
                    handleEditUserRoles(selectedUser);
                } else {
                    handleSnackbarOpen('Failed to add role', 'error');
                }
            } catch (error) {
                console.error('Error adding role:', error);
                handleSnackbarOpen('Error adding role', 'error');
            }
        }
    };

    const handleAddApplicationToUser = async () => {
        if (!selectedApplicationToAdd) {
            handleSnackbarOpen('Please select an application to add.', 'warning');
            return;
        } else {
            try {
                const response = await instance.post(`/access/apps/add`, { user_id: selectedUser.id, application_id: selectedApplicationToAdd }, { withCredentials: true });
                if (response.status === 200) {
                    handleSnackbarOpen('Application added successfully', 'success');
                    handleEditUserApplications(selectedUser);
                } else {
                    handleSnackbarOpen('Failed to add application', 'error');
                }
            } catch (error) {
                console.error('Error adding application:', error);
                handleSnackbarOpen('Error adding application', 'error');
            }
        }
    };

    const handleXferCRMUsers = async () => {
        try {
            setXferCRMUsers(true);
            setCrmUserSearchQuery(''); // Clear search when opening dialog
            setSelectedCrmUsers([]); // Clear selections when opening dialog
            const response = await instance.get('/users/crm/all', { withCredentials: true });
            if (response.status === 200) {
                const filteredUsers = response.data.data.users.filter((crmUser) => !users.some((user) => user.username === crmUser.username));
                setCrmUsers(filteredUsers);
            } else {
                console.error('Failed to fetch CRM users');
                handleSnackbarOpen('Failed to fetch CRM users', 'error');
            }
        } catch (error) {
            console.error('Error fetching CRM users:', error);
            handleSnackbarOpen('Error fetching CRM users', 'error');
        }
    };

    const handleTransferCRMUser = async (crmUserId) => {
        try {
            const response = await instance.post('/users/xfer', { crm_user_id: crmUserId }, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('User transferred successfully', 'success');
                const newUsers = users.filter((user) => user.id !== crmUserId);
                setUsers(newUsers);
                const newCRMUsers = crmUsers.filter((crmUser) => crmUser.id !== crmUserId);
                setCrmUsers(newCRMUsers);
                setRefreshUsers(true);
            } else {
                handleSnackbarOpen('Failed to transfer user', 'error');
            }
        } catch (error) {
            console.error('Error transferring user:', error);
            handleSnackbarOpen('Error transferring user', 'error');
        }
    };

    const handleCloseXferDialog = () => {
        setXferCRMUsers(false);
        setCrmUserSearchQuery(''); // Clear search when closing dialog
        setSelectedCrmUsers([]); // Clear selections when closing dialog
    };

    const handleSelectCrmUser = (crmUserId) => {
        setSelectedCrmUsers(prev => {
            if (prev.includes(crmUserId)) {
                return prev.filter(id => id !== crmUserId);
            } else {
                return [...prev, crmUserId];
            }
        });
    };

    const handleSelectAllCrmUsers = () => {
        if (selectedCrmUsers.length === filteredCrmUsers.length) {
            setSelectedCrmUsers([]);
        } else {
            setSelectedCrmUsers(filteredCrmUsers.map(user => user.id));
        }
    };

    const handleBatchTransferCrmUsers = async () => {
        if (selectedCrmUsers.length === 0) {
            handleSnackbarOpen('Please select at least one user to transfer', 'warning');
            return;
        }

        setIsTransferring(true);
        let successCount = 0;
        let errorCount = 0;
        const transferredUserIds = [];

        try {
            // Process users sequentially (caveman style as requested)
            for (const crmUserId of selectedCrmUsers) {
                try {
                    const response = await instance.post('/users/xfer', { crm_user_id: crmUserId }, { withCredentials: true });
                    if (response.status === 200) {
                        successCount++;
                        transferredUserIds.push(crmUserId);
                    } else {
                        errorCount++;
                    }
                } catch (error) {
                    console.error(`Error transferring user ${crmUserId}:`, error);
                    errorCount++;
                }
            }

            // Remove all successfully transferred users from CRM users list in one update
            if (transferredUserIds.length > 0) {
                const newCRMUsers = crmUsers.filter((crmUser) => !transferredUserIds.includes(crmUser.id));
                setCrmUsers(newCRMUsers);
            }

            // Show results
            if (errorCount === 0) {
                handleSnackbarOpen(`Successfully transferred ${successCount} user(s)`, 'success');
            } else if (successCount === 0) {
                handleSnackbarOpen(`Failed to transfer all ${errorCount} user(s)`, 'error');
            } else {
                handleSnackbarOpen(`Transferred ${successCount} user(s), ${errorCount} failed`, 'warning');
            }

            // Clear selections and refresh
            setSelectedCrmUsers([]);
            setRefreshUsers(true);
        } catch (error) {
            console.error('Error in batch transfer:', error);
            handleSnackbarOpen('Error during batch transfer', 'error');
        } finally {
            setIsTransferring(false);
        }
    };

    const handleRemoveReport = async (reportId) => {
        try {
            const response = await instance.post(`/access/delete`, { user_id: selectedUser.id, report_id: reportId }, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('Report removed successfully', 'success');
                handleEditUserReports(selectedUser);
            } else {
                handleSnackbarOpen('Failed to remove report', 'error');
            }
        } catch (error) {
            console.error('Error removing report:', error);
            handleSnackbarOpen('Error removing report', 'error');
        }
    };

    const handleRemoveApplication = async (applicationId) => {
        try {
            const response = await instance.post(`/access/apps/delete`, { user_id: selectedUser.id, application_id: applicationId }, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('Application removed successfully', 'success');
                handleEditUserApplications(selectedUser);
            } else {
                handleSnackbarOpen('Failed to remove application', 'error');
            }
        } catch (error) {
            console.error('Error removing application:', error);
            handleSnackbarOpen('Error removing application', 'error');
        }
    };

    const handleUserDelete = async (userId) => {
        try {
            const response = await instance.post(`/users/${userId}/delete`, {}, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('User deleted successfully', 'success');
                const newUsers = users.filter((user) => user.id !== userId);
                setUsers(newUsers);
            } else {
                handleSnackbarOpen('Failed to delete user', 'error');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            handleSnackbarOpen('Error deleting user', 'error');
        }
    };

    const handleDeleteUserClick = (user) => {
        setUserToDelete(user);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteUserConfirm = () => {
        if (userToDelete) {
            handleUserDelete(userToDelete.id);
            setDeleteConfirmOpen(false);
            setUserToDelete(null);
        }
    };

    const handleRemoveRole = async(roleId) => {
        try {
            const response = await instance.post(`/users/roles/members/remove`, { user_id: selectedUser.id, role_id: roleId }, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('Role removed successfully', 'success');
                handleEditUserRoles(selectedUser);
            } else {
                handleSnackbarOpen('Failed to remove role', 'error');
            }
        } catch (error) {
            console.error('Error removing role:', error);
            handleSnackbarOpen('Error removing role', 'error');
        }
    }

    useEffect(() => {
        const fetchAvailableReports = async () => {
            const response = await instance.post('/reports/all', {}, { withCredentials: true });
            if (response.status === 200) {
                setAvailableReports(response.data.data.reports);
            } else {
                console.error('Failed to fetch reports');
            }
        };
        fetchAvailableReports();
    }, []);

    useEffect(() => {
        const checkChangePasswordDisabled = () => {
            if (password && confirmPassword && password === confirmPassword && password.length > 0 && confirmPassword.length > 0) {
                setChangePasswordDisabled(false);
            } else {
                setChangePasswordDisabled(true);
            }
        }
        checkChangePasswordDisabled();
    }, [password, confirmPassword]);    

    useEffect(() => {
        const fetchAllRoles = async () => {
            try {
                const response = await instance.get('/users/roles/all', { withCredentials: true });
                setAllRoles(response.data.data.roles);
            } catch (error) {
                console.error('Error fetching roles', error);
            }
        };
        fetchAllRoles();
    }, []);

    useEffect(() => {
        const fetchAllRoles = async () => {
            try {
                const response = await instance.get('/users/roles/all', { withCredentials: true });
                setAllRoles(response.data.data.roles);
            } catch (error) {
                console.error('Error fetching roles', error);
            }
        };
        fetchAllRoles();
        setRefreshUsers(false);
    }, [refreshUsers]);

    useEffect(() => {
        const fetchAvailableApplications = async () => {
            const response = await instance.post('/applications/all', {}, { withCredentials: true });
            if (response.status === 200) {
                setAvailableApplications(response.data.data.apps);
            } else {
                console.error('Failed to fetch applications');
            }
        };
        fetchAvailableApplications();
    }, []);

    const handleEditUserReports = async (user) => {
        setSelectedUser(user);
        const response = await instance.post(`/users/${user.id}/access_list`, {}, { withCredentials: true });
        if (response.status === 200) {
            setUserReports(response.data.data.access);
        } else {
            console.error('Failed to fetch user reports');
        }
        setEditUserReportsDialogOpen(true);
    };

    const handleUserReportClear = () => {
        setSelectedReportToAdd('');
        setEditUserReportsDialogOpen(false);
    };

    const handleEditUserRoles = async (user) => {
        setSelectedUser(user);
        const response = await instance.post(`/users/view/${user.id}`, {}, { withCredentials: true });
        if (response.status === 200) {
            setUserRoles(response.data.data.roles);
        } else {
            console.error('Failed to fetch user roles');
        }
        setEditUserRolesDialogOpen(true);
    };

    const handleUserRoleClear = () => {
        setSelectedRoleToAdd('');
        setEditUserRolesDialogOpen(false);
    };

    const handleEditUserApplications = async (user) => {
        setSelectedUser(user);
        const response = await instance.post(`/users/${user.id}/apps/access_list`, {}, { withCredentials: true });
        if (response.status === 200) {
            setUserApplications(response.data.data.access);
        } else {
            console.error('Failed to fetch user applications');
        }
        setEditUserApplicationsDialogOpen(true);
    };

    const handleUserApplicationClear = () => {
        setSelectedApplicationToAdd('');
        setEditUserApplicationsDialogOpen(false);
    };

    useEffect(() => {
        const checkSubmitDisabled = () => {
            if ((userName && userEmail && userPassword && userConfirmPassword) && (userPassword === userConfirmPassword)) {
                setSubmitDisabled(false);
            } else {
                setSubmitDisabled(true);
            }
        }
        checkSubmitDisabled();
    }, [userName, userEmail, userPassword, userConfirmPassword]);

    useEffect(() => {
        const fetchUsers = async () => {
            const response = await instance.post('/users/all', {}, { withCredentials: true });
            if (response.status === 200) {
                setUsers(response.data.data.users);
            } else {
                console.log('Error fetching users');
            }
        }
        fetchUsers();
        setChecked(true);
    }, [xferCRMUsers, refreshUsers]);

    const handleEditUser = async (user) => {
        const userAttributes = await instance.post(`users/view/${user.id}`, {}, { withCredentials: true });
        if (userAttributes.status === 200) {
            setUserAttributes(userAttributes.data.data);
            setId(userAttributes.data.data.id);
            setEmail(userAttributes.data.data.email);
            setIsAdmin(userAttributes.data.data.is_admin);
            setUsername(userAttributes.data.data.username);
        }
        setSelectedUser(user);
        setEditUserDialogOpen(true);
    }

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
    }

    const handleIsAdminChange = (e, user) => {
        setIsAdmin(e.target.checked);
        setUserAdminCondition(user.is_admin);
        setIsAdminChange(true);
    }

    const handleUsernameChange = (e) => {
        setUsername(e.target.value);
    }

    const handleEditUserAttributes = async (id) => {
        try {
            const response = await instance.post(`users/${id}/edit`, { email, is_admin: isAdmin, username }, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('User attributes updated successfully', 'success');
                setEditUserDialogOpen(false);
                setRefreshUsers(true);
                setUserAttributes([]);
                setId('');
                setEmail('');
                setIsAdmin(false);
                setUsername('');
                setSelectedUser(null);
            } else {
                handleSnackbarOpen('Failed to update user attributes', 'error');
            }
        } catch (error) {
            console.error('Error updating user attributes:', error);
            handleSnackbarOpen('Error updating user attributes', 'error');
        }
    }

    const handleSubmitAddUser = async () => {
        try {
            const response = await instance.post('/users/add', { username: userName, email: userEmail, password: userPassword }, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('User added successfully', 'success');
                setUserName('');
                setUserEmail('');
                setUserPassword('');
                setUserConfirmPassword('');
                setAddUser(false);
                setRefreshUsers(true);
            } else {
                handleSnackbarOpen('Failed to add user', 'error');
            }
        } catch (error) {
            console.error('Error adding user:', error);
            handleSnackbarOpen('Error adding user', 'error');
        }
    }

    return (
        <div>
            <h2 style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '10vh',
                fontFamily: "Lato"
            }}>User Management</h2>
            <Button variant='contained' onClick={() => handleToggleAddUser()}>Add User</Button>
            <Button variant='contained' onClick={() => handleXferCRMUsers()} style={{
                marginLeft: '2vh'
            }}>Transfer CRM Users</Button>
            {addUser && (
                <Dialog open={addUser} onClose={handleAddUserClear}>
                    <DialogTitle>Add User</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Enter user information
                        </DialogContentText>
                        <TextField
                            autoFocus
                            margin='dense'
                            id='name'
                            label='Username'
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            type='text'
                            fullWidth
                            required
                        />
                        <TextField
                            margin='dense'
                            id='email'
                            label='Email'
                            value={userEmail}
                            onChange={(e) => setUserEmail(e.target.value)}
                            type='email'
                            fullWidth
                            required
                        />
                        <TextField
                            margin='dense'
                            id='password'
                            label='Password'
                            value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            type='password'
                            fullWidth
                            required
                        />
                        <TextField
                            margin='dense'
                            id='confirm-password'
                            label='Confirm Password'
                            value={userConfirmPassword}
                            onChange={(e) => setUserConfirmPassword(e.target.value)}
                            type='password'
                            fullWidth
                            required
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleAddUserClear} color='primary'>
                            Cancel
                        </Button>
                        <Button onClick={() => handleSubmitAddUser()} color='primary' disabled={submitDisabled}>
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <div>
                    <TableContainer>
                    <Fade in={checked}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>User ID</TableCell>
                                <TableCell>User Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Admin</TableCell>
                                <TableCell>Active</TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        {users.map((user, index) => (
                        <TableBody>
                        <TableRow key={index}>
                            <TableCell>{user.id}</TableCell>
                            <TableCell>{user.username}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.is_admin ? 'Yes' : 'No'}</TableCell>
                            <TableCell><StatusIcon isActive={user.is_active} /></TableCell>
                            <TableCell>
                            <Button onClick={() => handleEditUserReports(user)} disabled={user.is_admin}>Reports</Button>
                            </TableCell>
                            <TableCell>
                            <Button onClick={() => handleEditUserRoles(user)}>Roles</Button>
                            </TableCell>
                            <TableCell>
                            <Button onClick={() => handleEditUser(user)}>Edit User</Button>
                            </TableCell>
                            <TableCell>
                            <Button onClick={() => handleEditUserApplications(user)}>Apps</Button>
                            </TableCell>
                            <TableCell>
                            <Button color="error" onClick={() => handleDeleteUserClick(user)}>Delete User</Button>
                            </TableCell>
                        </TableRow>
                        </TableBody>
                        ))}
                    </Table>
                    </Fade>
                    </TableContainer>
            </div>
            <Dialog open={editUserReportsDialogOpen} onClose={handleUserReportClear}>
                <DialogTitle>{selectedUser ? `${selectedUser.username}'s Reports` : 'User Reports'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                    Reports accessible by user.
                    </DialogContentText>
                    <div className='table-management'>
                    <Table>
                        <TableHead>
                        <TableRow>
                            <TableCell>Report ID</TableCell>
                            <TableCell>Report Name</TableCell>
                            <TableCell>Remove</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {userReports.map((report, index) => (
                            <TableRow key={index}>
                            <TableCell>{report.id}</TableCell>
                            <TableCell>{report.name}</TableCell>
                            <TableCell>
                                <Button variant='contained' onClick={() => handleRemoveReport(report.id)}>Remove</Button>
                            </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={2}>
                            <TextField
                                select
                                label="Select Report to Add"
                                fullWidth
                                margin="dense"
                                value={selectedReportToAdd}
                                onChange={(e) => setSelectedReportToAdd(e.target.value)}
                            >
                                {availableReports.map((report) => (
                                <MenuItem key={report.id} value={report.id}>
                                    {report.name}
                                </MenuItem>
                                ))}
                            </TextField>
                            </TableCell>
                            <TableCell>
                            <Button onClick={handleAddReportToUser} color='primary' variant='contained'>
                                Add Selected Report
                            </Button>
                            </TableCell>
                        </TableRow>
                        </TableBody>
                    </Table>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUserReportClear} color='primary'>
                    Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={editUserRolesDialogOpen} onClose={handleUserRoleClear}>
                <DialogTitle>{selectedUser ? `${selectedUser.username}'s Roles` : 'User Roles'}</DialogTitle>
                <DialogContent style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidTableCell: '600px'
                }}>
                    <DialogContentText>
                    Roles assigned to TableCelle user.
                    </DialogContentText>
                    <div className='table-management'>
                    <Table>
                        <TableHead>
                        <TableRow>
                            <TableCell>Role ID</TableCell>
                            <TableCell>Role Name</TableCell>
                            <TableCell>Remove</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {userRoles.map((role, index) => (
                            <TableRow key={index}>
                            <TableCell>{role.role_id}</TableCell>
                            <TableCell>{role.role_name}</TableCell>
                            <TableCell>
                                <Button variant='contained' onClick={() => handleRemoveRole(role.role_id)}>Remove</Button>
                            </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={2}>
                            <TextField
                                select
                                label="Select Role to Add"
                                fullWidth
                                margin="dense"
                                value={selectedRoleToAdd}
                                onChange={(e) => setSelectedRoleToAdd(e.target.value)}
                            >
                                {allRoles.map((role) => (
                                    <MenuItem key={role.role_id} value={role.role_id}>
                                    {role.role_name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            </TableCell>
                            <TableCell>
                            <Button onClick={() => handleAddRoleToUser()} color='primary' variant='contained'>
                                Add Selected Role
                            </Button>
                            </TableCell>
                        </TableRow>
                        </TableBody>
                    </Table>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUserRoleClear} color='primary'>
                    Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={editUserApplicationsDialogOpen} onClose={handleUserApplicationClear}>
                <DialogTitle>{selectedUser ? `${selectedUser.username}'s Applications` : 'User Applications'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                    Applications accessible by user.
                    </DialogContentText>
                    <div>
                    <TableContainer>
                    <Table>
                        <TableHead>
                        <TableRow>
                            <TableCell>Application ID</TableCell>
                            <TableCell>Application Name</TableCell>
                            <TableCell>Remove</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {userApplications.map((application, index) => (
                            <TableRow key={index}>
                            <TableCell>{application.id}</TableCell>
                            <TableCell>{application.name}</TableCell>
                            <TableCell>
                                <Button variant='contained' onClick={() => handleRemoveApplication(application.id)}>Remove</Button>
                            </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={2}>
                            <TextField
                                select
                                label="Select Application to Add"
                                fullWidth
                                margin="dense"
                                value={selectedApplicationToAdd}
                                onChange={(e) => setSelectedApplicationToAdd(e.target.value)}
                            >
                                {availableApplications.map((application) => (
                                <MenuItem key={application.id} value={application.id}>
                                    {application.name}
                                </MenuItem>
                                ))}
                            </TextField>
                            </TableCell>
                            <TableCell>
                            <Button onClick={handleAddApplicationToUser} color='primary' variant='contained'>
                                Add Selected Application
                            </Button>
                            </TableCell>
                        </TableRow>
                        </TableBody>
                    </Table>
                    </TableContainer>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUserApplicationClear} color='primary'>
                    Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog 
                open={xferCRMUsers} 
                onClose={handleCloseXferDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        minHeight: '500px',
                        maxHeight: '80vh',
                        width: '90vw',
                        maxWidth: '800px'
                    }
                }}
            >
                <DialogTitle>Transfer CRM Users</DialogTitle>
                <DialogContent style={{ minHeight: '400px' }}>
                    <DialogContentText>
                        Select a CRM user to transfer to system.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        label="Search users..."
                        value={crmUserSearchQuery}
                        onChange={(e) => setCrmUserSearchQuery(e.target.value)}
                        margin="normal"
                        placeholder="Search by name or email..."
                    />
                    <div style={{ 
                        minHeight: '300px',
                        maxHeight: '300px', 
                        overflowY: 'auto',
                        marginTop: '16px'
                    }}>
                        <Table>
                            <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Checkbox
                                        checked={selectedCrmUsers.length === filteredCrmUsers.length && filteredCrmUsers.length > 0}
                                        indeterminate={selectedCrmUsers.length > 0 && selectedCrmUsers.length < filteredCrmUsers.length}
                                        onChange={handleSelectAllCrmUsers}
                                    />
                                </TableCell>
                                <TableCell>CRM User ID</TableCell>
                                <TableCell>CRM User Name</TableCell>
                                <TableCell>Transfer</TableCell>
                            </TableRow>
                            </TableHead>
                            <TableBody>
                            {filteredCrmUsers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} style={{ textAlign: 'center', padding: '40px', height: '200px' }}>
                                        {crmUserSearchQuery ? 'No users found matching your search.' : 'No CRM users available to transfer.'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCrmUsers.map((crmUser, index) => (
                                    <TableRow key={index}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selectedCrmUsers.includes(crmUser.id)}
                                            onChange={() => handleSelectCrmUser(crmUser.id)}
                                        />
                                    </TableCell>
                                    <TableCell>{crmUser.id}</TableCell>
                                    <TableCell>{crmUser.name}</TableCell>
                                    <TableCell>
                                        <Button variant='contained' onClick={() => handleTransferCRMUser(crmUser.id)}>Transfer</Button>
                                    </TableCell>
                                    </TableRow>
                                ))
                            )}
                            </TableBody>
                        </Table>
                    </div>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'space-between' }}>
                    <div>
                        {selectedCrmUsers.length > 0 && (
                            <span style={{ marginRight: '16px', color: '#666' }}>
                                {selectedCrmUsers.length} user(s) selected
                            </span>
                        )}
                    </div>
                    <div>
                        <Button 
                            onClick={handleBatchTransferCrmUsers} 
                            variant='contained' 
                            color='primary'
                            disabled={selectedCrmUsers.length === 0 || isTransferring}
                            style={{ marginRight: '8px' }}
                        >
                            {isTransferring ? 'Transferring...' : `Transfer Selected (${selectedCrmUsers.length})`}
                        </Button>
                        <Button onClick={handleCloseXferDialog} color='primary'>
                            Cancel
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
            <Dialog
                open={editUserDialogOpen}
                onClose={() => {
                    setEditUserDialogOpen(false);
                    setUserAdminCondition(null);
                }}
                maxWidth="md"
                fullWidth
                >
                <DialogTitle>{selectedUser ? `Edit ${selectedUser.username}` : 'Edit User'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Update the user information below.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        type="text"
                        label="User ID"
                        variant="outlined"
                        value={id}
                        disabled
                        margin="dense"
                        helperText="User ID cannot be changed"
                    />
                    <TextField
                        fullWidth
                        type="email"
                        label="Email"
                        variant="outlined"
                        value={email}
                        onChange={handleEmailChange}
                        helperText={!email.includes('@') && email ? "Must be a valid email address" : "Enter the user's email address"}
                        required
                        margin="dense"
                        error={!email.includes('@') && email}
                    />
                    <TextField
                        fullWidth
                        type="text"
                        label="Username"
                        variant="outlined"
                        value={username}
                        onChange={handleUsernameChange}
                        helperText="Enter the user's username"
                        required
                        margin="dense"
                    />
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px', marginBottom: '16px' }}>
                        <Checkbox
                            checked={isAdmin}
                            onChange={(e) => handleIsAdminChange(e, selectedUser)}
                        />
                        <span>Admin User</span>
                    </div>
                    <Button 
                        variant="outlined" 
                        onClick={() => handleSetChangePassword()}
                        style={{ marginTop: '16px' }}
                    >
                        Change Password
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditUserDialogOpen(false)} color='primary'>
                        Cancel
                    </Button>
                    <Button onClick={() => handleEditUserAttributes(selectedUser.id)} color='primary'>
                        Update User
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={changePasswordBox} onClose={() => setChangePasswordBox(false)}>
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Enter new password.
                    </DialogContentText>
                    <TextField
                        type='password'
                        label='New Password'
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        margin='dense'
                    />
                    <TextField
                        type='password'
                        label='Confirm New Password'
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        margin='dense'
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setChangePasswordBox(false)} color='primary'>
                        Cancel
                    </Button>
                    <Button onClick={() => handleChangePassword(id)} color='primary' disabled={changePasswordDisabled}>
                        Change
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isadminChange} onClose={() => setIsAdminChange(false)}>
                <DialogTitle>Warning</DialogTitle>
                <DialogContent>
                    {userAdminCondition ? (
                        <DialogContentText>
                            Removing a user's admin status will remove all access to resources.
                        </DialogContentText>
                    ) : (
                        <DialogContentText>
                            Changing a user to an admin will give them full access to the system.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setIsAdminChange(false);
                            setIsAdmin(!isAdmin);
                        }} color='primary'>
                        Cancel
                    </Button>
                    <Button onClick={() => setIsAdminChange(false)} color='primary'>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setUserToDelete(null);
                }}
                onConfirm={handleDeleteUserConfirm}
                title="Delete User"
                resourceType="User"
                resourceName={userToDelete?.username || userToDelete?.email}
                requireTypeConfirm={true}
                confirmButtonText="Delete User"
            />
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ widTableCell: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default UserManagement;
